import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/AlliedTelesis.css";
import alliedTelesisLogo from "../../assets/images/partners/Allied Telesis.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const archLayers = [
  { icon: "🌐", name: "WAN / Internet Edge", tag: "Layer 3", desc: "High-availability SD-WAN edge with dual ISP failover and BGP routing" },
  { icon: "🔐", name: "Firewall / UTM Zone", tag: "Security", desc: "Next-gen firewall with IPS, content filtering, and VPN gateway" },
  { icon: "⚡", name: "Core Distribution Layer", tag: "Layer 3 Switch", desc: "10/25/40G fiber aggregation with L3 VLAN routing and redundancy" },
  { icon: "🏢", name: "Access Layer", tag: "Layer 2 Switch", desc: "PoE+ access switching for endpoints, IP phones, and Wi-Fi APs" },
  { icon: "📡", name: "Wi-Fi Access Layer", tag: "802.11ax", desc: "Enterprise Wi-Fi with cloud management and WLAN controller" },
];

const switchCards = [
  { series: "x930 Series", icon: "⚡", name: "Data Center Core Switching", desc: "High-density 10/25/40/100G switches for data center fabric and campus core deployments — with hardware-based VXLAN, EVPN, and OpenFlow support for SDN-ready infrastructure.", specs: ["100G Uplinks", "VXLAN/EVPN", "OpenFlow 1.3", "SDN Ready"] },
  { series: "x550 Series", icon: "🔗", name: "Distribution & Aggregation", desc: "Multi-gigabit distribution layer switches aggregating access layer traffic with full L3 routing, RIP/OSPF/BGP support, and stacking for simplified management.", specs: ["Multi-Gig PoE", "L3 Routing", "OSPF/BGP", "VRF Lite"] },
  { series: "x230 Series", icon: "🏢", name: "PoE+ Access Switching", desc: "Intelligent PoE+ access switches powering IP cameras, VoIP phones, Wi-Fi APs, and IoT devices — with per-port PoE scheduling and intelligent power management.", specs: ["PoE+ 30W/port", "Per-Port Control", "VLANs", "RADIUS Auth"] },
  { series: "IE Series", icon: "🏭", name: "Industrial Ethernet", desc: "Hardened industrial switches rated for -40°C to +75°C operation — M12 connectors, DIN rail mounting, IEEE 1588 PTP precision timing, and PROFINET/EtherNet/IP support.", specs: ["IP30/IP40", "-40 to 75°C", "IEEE 1588", "PROFINET"] },
  { series: "SBx908 GEN2", icon: "🌐", name: "Campus Routing & SD-WAN", desc: "Campus core router with integrated SD-WAN capabilities, hardware-accelerated VPN, and dynamic path selection — reducing WAN costs while improving application performance.", specs: ["SD-WAN", "VPN HW Accel", "Dynamic Path", "QoS"] },
  { series: "CentreCOM", icon: "🔌", name: "Fiber Connectivity", desc: "Media converters, SFP transceivers, and fiber switches extending Ethernet over single-mode and multimode fiber — connecting geographically distributed buildings and campus locations.", specs: ["SM/MM Fiber", "SFP/SFP+/QSFP", "Bidirectional", "WDM Support"] },
];

const industrialFeats = [
  { icon: "🌡️", title: "Wide Temperature Range", desc: "Rated -40°C to +75°C for factory floors, outdoor cabinets, and transportation environments without active cooling." },
  { icon: "⚙️", title: "M12 Industrial Connectors", desc: "Vibration-resistant M12 locking connectors prevent cable disconnection in high-vibration manufacturing environments." },
  { icon: "⏱️", title: "IEEE 1588 PTP Timing", desc: "Sub-microsecond precision time synchronization for motion control, SCADA systems, and time-sensitive industrial applications." },
  { icon: "🔄", title: "Ring Redundancy (EPSR)", desc: "EPSR (Ethernet Protected Switched Ring) provides sub-50ms failover for industrial ring topologies — critical for continuous manufacturing processes." },
  { icon: "📡", title: "PROFINET & EtherNet/IP", desc: "Native support for industrial protocols alongside standard Ethernet — enabling unified OT/IT network infrastructure without protocol gateways." },
  { icon: "🛡️", title: "Cyber-Secure by Design", desc: "IEC 62443 security alignment, 802.1X authentication for OT devices, and port security preventing unauthorized devices on industrial networks." },
];

const campusSolutions = [
  { icon: "🔒", title: "Allied Telesis Network Access Control", desc: "AlliedWare Plus integrated NAC with 802.1X and MAC-based authentication — quarantining non-compliant devices before network access is granted, without requiring third-party NAC appliances." },
  { icon: "📊", title: "AMF (Autonomous Management Framework)", desc: "Zero-touch provisioning and automated network management for multi-site campus deployments — new switches automatically receive configuration from the AMF master without manual intervention." },
  { icon: "🤖", title: "AI-Driven Troubleshooting", desc: "Allied Telesis Autonomous Management Framework Smart Proxy detects and isolates failing links, loops, and misconfigured ports automatically — reducing MTTR without engineer involvement." },
  { icon: "🏫", title: "Guest Network Automation", desc: "Automated guest VLAN provisioning through Allied Telesis captive portal — with time-limited access, bandwidth throttling, and automatic deprovisioning after session expiry." },
  { icon: "📡", title: "Unified Wired + Wireless", desc: "Allied Telesis Vista Manager EX provides single-pane management for both Allied Telesis switching and wireless access points — eliminating separate wired and wireless management tools." },
];

const timelineItems = [
  { side: "left", icon: "📐", phase: "Phase 1", title: "Network Discovery & Assessment", desc: "Comprehensive discovery of existing network topology, traffic patterns, VLAN structure, and security policy baseline — identifying gaps before Allied Telesis design begins." },
  { side: "right", icon: "🗺️", phase: "Phase 2", title: "Reference Architecture Design", desc: "Allied Telesis solution architecture aligned to your site layout, user density, bandwidth requirements, and growth plans — with VLAN design, routing topology, and redundancy strategy." },
  { side: "left", icon: "🔧", phase: "Phase 3", title: "Staged Lab Validation", desc: "Pre-production testing of switch configurations, VLAN policies, routing protocols, and AMF provisioning in a lab environment before production rollout begins." },
  { side: "right", icon: "🚀", phase: "Phase 4", title: "Zero-Touch Production Deployment", desc: "AMF-orchestrated switch deployment with automated configuration — new switches provision automatically, minimizing change window duration and technician error." },
  { side: "left", icon: "✅", phase: "Phase 5", title: "Validation & Handover", desc: "Post-deployment validation confirming all network paths, redundancy failover, security policies, and monitoring integration — with full documentation and team training." },
];

const industries = [
  { icon: "🏥", name: "Healthcare", desc: "PoE+ access switching for medical IoT, clinical workstation connectivity, and staff wireless with 802.1X authentication for HIPAA-compliant network access." },
  { icon: "🏭", name: "Industrial & OT", desc: "Hardened industrial switches for factory floors and process control environments — PROFINET/EtherNet/IP support and IEC 62443-aligned security for OT networks." },
  { icon: "🎓", name: "Education", desc: "AMF zero-touch campus deployment for K-12 and higher education — automated student VLAN provisioning, Wi-Fi authentication, and centralized management across distributed buildings." },
  { icon: "🏛️", name: "Government", desc: "Secure campus switching for government buildings with RADIUS authentication, 802.1X enforcement, and audit logging meeting NIST SP 800-53 network access control requirements." },
  { icon: "🏢", name: "Enterprise Campus", desc: "Multi-building campus switching fabric with AMF management, OSPF routing, and redundant fiber interconnects supporting high-availability operations." },
  { icon: "⚡", name: "Utilities & Energy", desc: "Substation hardened switching and fiber ring networks for utilities — IEEE 1588 precision timing, GOOSE protocol support, and substation-rated temperature range." },
  { icon: "🚂", name: "Transportation", desc: "Ruggedized switching for rail, bus, and transit systems — M12 connectors, wide temperature operation, and EN 50155 railway standard compliance." },
  { icon: "🏨", name: "Hospitality", desc: "PoE+ switching for IPTV, IP phones, and Wi-Fi APs — with per-room VLAN isolation, guest network automation, and centralized Vista Manager EX dashboard." },
];

const reasons = [
  { icon: "🏆", title: "Allied Telesis Authorized Partner", desc: "ALG Data Guard's direct Allied Telesis partnership provides access to full product catalog, pre-sales technical support, and partner pricing for enterprise networking projects." },
  { icon: "🔧", title: "Network Engineering Specialists", desc: "Certified network engineers designing Allied Telesis solutions with Layer 2/3 expertise, VLAN architecture experience, and industrial networking knowledge." },
  { icon: "⚙️", title: "AMF Deployment Expertise", desc: "ALG Data Guard has deployed Allied Telesis AMF in multi-site environments — providing zero-touch provisioning, automated failover configuration, and centralized management." },
  { icon: "📊", title: "Ongoing Network Management", desc: "Proactive network monitoring, patch management, configuration backup, and change management for Allied Telesis environments through managed network services." },
];

const faqData = [
  { q: "What is Allied Telesis AMF (Autonomous Management Framework) and how does it simplify network management?", a: "Allied Telesis AMF (Autonomous Management Framework) is a proprietary network management technology that treats all Allied Telesis switches in a network as a single logical device. AMF eliminates most manual network management tasks: when you add a new switch to an AMF network, it automatically downloads its configuration from the AMF master — no manual CLI configuration required. When a switch fails, its replacement automatically restores the same configuration from AMF backup — typically within minutes rather than hours of manual re-configuration. AMF also enables network-wide software upgrades with a single command, centralized firmware management, and automatic configuration backup of all devices. For organizations with multiple buildings or locations, AMF dramatically reduces the time technicians spend on routine switch management tasks. ALG Data Guard deploys AMF across all Allied Telesis environments we manage — as it reduces both deployment time and ongoing operational burden." },
  { q: "What is the difference between Allied Telesis AlliedWare Plus switches and the IE Industrial Ethernet series?", a: "Allied Telesis AlliedWare Plus switches are enterprise-grade networking equipment designed for commercial indoor environments — controlled temperature, humidity, and physical conditions. They provide advanced L2/L3 features, AMF support, and comprehensive security capabilities appropriate for office buildings, data centers, and standard campus deployments. Allied Telesis IE (Industrial Ethernet) Series switches are hardened versions designed for demanding industrial environments: operating temperature ranges of -40°C to +75°C, M12 locking connectors for vibration resistance, IP30/IP40 rated enclosures for dust and moisture protection, DIN rail mounting for cabinet installation, and support for industrial protocols like PROFINET, EtherNet/IP, and MODBUS TCP. IE Series switches are appropriate for factory floors, outdoor cabinets, transportation vehicles, utility substations, and any environment where commercial-grade equipment would fail due to environmental conditions. ALG Data Guard assesses your deployment environment to recommend the appropriate Allied Telesis product series." },
  { q: "How does Allied Telesis Vista Manager EX compare to other network management platforms?", a: "Allied Telesis Vista Manager EX is Allied Telesis's unified network management application providing visibility and control over both Allied Telesis wired switching and wireless infrastructure from a single interface. It presents a visual topology map of the network, real-time interface status, traffic monitoring, AMF network management, firmware upgrade coordination, and configuration management. Vista Manager EX integrates with Allied Telesis AMF — showing the complete AMF network topology automatically and enabling network-wide operations from the GUI without requiring CLI knowledge. Compared to vendor-agnostic platforms like SolarWinds or PRTG, Vista Manager EX has deeper Allied Telesis integration but is limited to Allied Telesis equipment. For multi-vendor environments, ALG Data Guard integrates Vista Manager EX with broader monitoring platforms to maintain single-pane visibility across all network infrastructure." },
  { q: "What fiber connectivity options does Allied Telesis provide?", a: "Allied Telesis provides comprehensive fiber connectivity solutions for extending networks between buildings and campus locations: SFP, SFP+, QSFP+, and QSFP28 transceivers for 1G, 10G, 25G, 40G, and 100G fiber links in Allied Telesis switches — supporting both single-mode (longer distance, typically up to 10km or 80km with appropriate optics) and multimode (shorter distance, up to 550m) fiber types. Bidirectional SFP transceivers enabling two fibers worth of throughput over a single fiber strand — reducing fiber count when cable plant is limited. WDM (Wavelength Division Multiplexing) solutions multiplexing multiple 1G links over a single fiber pair — extracting more capacity from existing fiber infrastructure. CentreCOM media converters extending Ethernet over fiber for locations where fiber-capable switches aren't deployed. ALG Data Guard performs fiber pathway assessments before recommending the appropriate transceiver type and distance capability for your specific building-to-building connectivity requirements." },
  { q: "How does ALG Data Guard manage Allied Telesis networks after deployment?", a: "ALG Data Guard provides comprehensive managed network services for Allied Telesis environments: proactive network monitoring with SNMP polling and syslog aggregation detecting issues before they impact operations; AMF network health monitoring with automated alerting for link failures, device reboots, and configuration drift; firmware management coordinating Allied Telesis AlliedWare Plus version upgrades during scheduled maintenance windows with rollback capability; configuration backup management ensuring all device configurations are backed up before changes and after modifications; VLAN and network policy change management for adding new segments, modifying security policies, or onboarding new sites; incident response for network outages with remote diagnostics and escalation to Allied Telesis TAC when hardware faults are identified; and quarterly network health reviews covering capacity utilization, redundancy status, and technology lifecycle planning for refresh decisions." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function AlliedTelesis() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [statsRef, statsInView] = useInView(0.3);
  const c1 = useCounter(50, 2000, statsInView);
  const c2 = useCounter(120, 2200, statsInView);
  const c3 = useCounter(4000, 2400, statsInView);
  const c4 = useCounter(30, 2000, statsInView);

  return (
    <div className="at-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={alliedTelesisLogo} alt="Allied Telesis" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Enterprise Networking & Industrial Ethernet Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Allied Telesis</h1>
            <p className="pd-identity-desc">
              Allied Telesis is a global leader in enterprise networking, industrial Ethernet, and campus switching solutions — serving organizations in healthcare, education, government, manufacturing, and transportation across 50 countries. Known for the Autonomous Management Framework (AMF) that revolutionizes zero-touch network management, and the IE Series industrial switches that withstand the harshest operating environments, Allied Telesis delivers enterprise-grade networking built for reliability. ALG Data Guard is an authorized Allied Telesis partner providing network design, deployment, and managed network services.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="at-hero at-blueprint-bg">
        <div className="at-hero__glow" aria-hidden="true" />
        {/* Topology diagram — purely decorative */}
        <div className="at-topology" aria-hidden="true">
          {[
            { top:"45%", left:"45%", cls:"at-topo-node--core", icon:"🌐" },
            { top:"10%", left:"30%", cls:"", icon:"📡" },
            { top:"10%", left:"62%", cls:"", icon:"🔒" },
            { top:"80%", left:"20%", cls:"", icon:"⚡" },
            { top:"80%", left:"70%", cls:"", icon:"🏢" },
            { top:"50%", left:"5%", cls:"", icon:"🖥️" },
            { top:"50%", left:"88%", cls:"", icon:"📱" },
          ].map((n, i) => (
            <div key={i} className={`at-topo-node ${n.cls}`} style={{ top:n.top, left:n.left, transform:"translate(-50%,-50%)" }}>
              {n.icon}
            </div>
          ))}
        </div>

        <div className="at-hero__inner">
          <div>
            <div className="at-label at-label--dark"><span className="at-dot" /> Allied Telesis Authorized Partner</div>
            <h2 className="at-display at-hero__title" style={{ color:"#fff" }}>
              Enterprise<br />Networks<br /><span>Built to Last.</span>
            </h2>
            <p className="at-hero__body">
              Allied Telesis delivers enterprise switching, industrial Ethernet, and intelligent network management for organizations where network reliability is non-negotiable. From PoE+ campus access switches to hardened industrial switches withstanding -40°C factory floors — Allied Telesis builds network infrastructure that operates continuously, managed centrally through AMF. ALG Data Guard designs and deploys Allied Telesis networks across every industry vertical.
            </p>
            <div className="at-hero__actions">
              <button className="at-btn at-btn--blue" onClick={() => navigate("/contact")}>
                Design My Network <ArrowIcon />
              </button>
              <button className="at-btn at-btn--ghost" onClick={() => navigate("/contact")}>
                Network Assessment
              </button>
            </div>
            <div className="at-hero__specs">
              <div className="at-hero__spec">
                <div className="at-hero__spec-val">AMF</div>
                <div className="at-hero__spec-label">Zero-Touch Mgmt</div>
              </div>
              <div className="at-hero__spec">
                <div className="at-hero__spec-val">-40°C</div>
                <div className="at-hero__spec-label">Industrial Range</div>
              </div>
              <div className="at-hero__spec">
                <div className="at-hero__spec-val">50+</div>
                <div className="at-hero__spec-label">Countries</div>
              </div>
            </div>
          </div>

          {/* Architecture Panel */}
          <div>
            <div className="at-label at-label--dark" style={{ marginBottom:"1rem" }}><span className="at-dot" /> Layered Network Architecture</div>
            <div className="at-arch-panel">
              {archLayers.map(layer => (
                <div key={layer.name} className="at-arch-layer">
                  <div className="at-arch-layer__head">
                    <span className="at-arch-layer__icon">{layer.icon}</span>
                    <span className="at-arch-layer__name">{layer.name}</span>
                    <span className="at-arch-layer__tag">{layer.tag}</span>
                  </div>
                  <p className="at-arch-layer__desc">{layer.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats Band ── */}
      <div className="at-stats-band" ref={statsRef}>
        <div className="at-shell">
          <div className="at-stats-band__inner">
            <div className="at-band-stat"><div className="at-band-stat__val">{c1}+</div><div className="at-band-stat__label">Countries with Deployments</div></div>
            <div className="at-band-stat"><div className="at-band-stat__val">{c2}+</div><div className="at-band-stat__label">Product SKUs</div></div>
            <div className="at-band-stat"><div className="at-band-stat__val">{c3}+</div><div className="at-band-stat__label">Enterprise Customers</div></div>
            <div className="at-band-stat"><div className="at-band-stat__val">{c3}+</div><div className="at-band-stat__label">Education Deployments</div></div>
            <div className="at-band-stat"><div className="at-band-stat__val">{c4}+ Yrs</div><div className="at-band-stat__label">Networking Innovation</div></div>
          </div>
        </div>
      </div>

      {/* ── Intelligent Architecture ── */}
      <section className="at-architecture">
        <div className="at-shell">
          <div className="at-architecture__inner">
            <img
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80"
              alt="Allied Telesis enterprise network infrastructure"
              className="at-architecture__img"
            />
            <div>
              <div className="at-label at-label--blue"><span className="at-dot" /> Intelligent Network Architecture</div>
              <h2 className="at-title">Allied Telesis AMF — Networks That Manage Themselves</h2>
              <p className="at-sub" style={{ marginBottom:"1.2rem" }}>Allied Telesis Autonomous Management Framework (AMF) transforms traditional network management by treating all switches in a network as a single logical unit — enabling zero-touch provisioning, automated failover, and single-command network-wide operations that eliminate most routine management tasks.</p>
              <p className="at-sub" style={{ marginBottom:"1.8rem" }}>ALG Data Guard deploys AMF as the management backbone for every Allied Telesis environment — reducing deployment time, minimizing operational overhead, and eliminating manual per-device configuration that creates inconsistency and error risk.</p>
              <div className="at-architecture__pillars">
                {[
                  { icon: "🤖", title: "Zero-Touch Provisioning", desc: "New switches automatically download configuration from AMF master on first boot — no manual CLI session required." },
                  { icon: "🔄", title: "Automatic Configuration Backup", desc: "AMF continuously backs up all device configurations — failed switch replacement restores in minutes, not hours." },
                  { icon: "🌐", title: "Single-Command Network Operations", desc: "Network-wide firmware upgrades, policy changes, and configuration updates deployed across all switches simultaneously." },
                  { icon: "🔍", title: "Automated Fault Detection", desc: "AMF detects network anomalies and isolates faults automatically — reducing MTTR without requiring engineer intervention." },
                ].map(p => (
                  <div key={p.title} className="at-pillar">
                    <span className="at-pillar__icon">{p.icon}</span>
                    <div>
                      <div className="at-pillar__title">{p.title}</div>
                      <p className="at-pillar__desc">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="at-btn at-btn--blue" onClick={() => navigate("/contact")}>
                  Explore AMF Deployment <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Enterprise Switching ── */}
      <section className="at-switching">
        <div className="at-shell">
          <div className="at-switching__head">
            <div className="at-label at-label--dark"><span className="at-dot" /> Enterprise Switching Portfolio</div>
            <h2 className="at-title">The Right Switch for Every Layer</h2>
            <p className="at-sub" style={{ margin:"0 auto", maxWidth:"600px" }}>Allied Telesis switching covers every layer from 100G data center core to ruggedized industrial Ethernet — with consistent AlliedWare Plus OS and AMF management across the entire portfolio.</p>
          </div>
          <div className="at-switching__grid">
            {switchCards.map(card => (
              <div key={card.name} className="at-switch-card">
                <div className="at-switch-card__series">{card.series}</div>
                <span className="at-switch-card__icon">{card.icon}</span>
                <h3 className="at-switch-card__name">{card.name}</h3>
                <p className="at-switch-card__desc">{card.desc}</p>
                <div className="at-switch-card__specs">
                  {card.specs.map(s => <span key={s} className="at-spec-chip">{s}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industrial Networking ── */}
      <section className="at-industrial">
        <div className="at-shell">
          <div className="at-industrial__inner">
            <div>
              <div className="at-label at-label--blue"><span className="at-dot" /> Industrial Ethernet</div>
              <h2 className="at-title">Networking for the Harshest Environments</h2>
              <p className="at-sub" style={{ marginBottom:"1.2rem" }}>Allied Telesis IE Series industrial switches operate where commercial networking equipment cannot — factory floors, outdoor enclosures, transportation vehicles, utility substations, and mining operations where temperature extremes, vibration, and environmental exposure would destroy standard switches.</p>
              <p className="at-sub" style={{ marginBottom:"1.8rem" }}>ALG Data Guard's industrial networking team designs Allied Telesis IE Series deployments with PROFINET and EtherNet/IP protocol support — enabling unified OT/IT network infrastructure that simplifies both operational technology and information technology management.</p>
              <div className="at-industrial__features">
                {industrialFeats.map(f => (
                  <div key={f.title} className="at-ind-feat">
                    <span className="at-ind-feat__icon">{f.icon}</span>
                    <div className="at-ind-feat__title">{f.title}</div>
                    <p className="at-ind-feat__desc">{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1565843708714-52ecf69ab81f?auto=format&fit=crop&w=900&q=80"
              alt="Industrial network infrastructure"
              className="at-industrial__img"
            />
          </div>
        </div>
      </section>

      {/* ── Secure Campus ── */}
      <section className="at-campus at-blueprint-bg">
        <div className="at-shell">
          <div className="at-campus__inner">
            <img
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=900&q=80"
              alt="Secure campus networking"
              className="at-campus__img"
            />
            <div>
              <div className="at-label at-label--dark"><span className="at-dot" /> Secure Campus Solutions</div>
              <h2 className="at-title at-campus__title">Campus Switching That Secures Itself</h2>
              <p className="at-campus__body">Allied Telesis campus solutions combine intelligent PoE+ access switching, integrated Network Access Control, and the Autonomous Management Framework into a unified campus network that is secure, self-managing, and highly available — without requiring separate NAC appliances or complex management infrastructure.</p>
              <div className="at-campus__solutions">
                {campusSolutions.map(s => (
                  <div key={s.title} className="at-campus-sol">
                    <span className="at-campus-sol__icon">{s.icon}</span>
                    <div>
                      <div className="at-campus-sol__title">{s.title}</div>
                      <p className="at-campus-sol__desc">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="at-btn at-btn--blue" onClick={() => navigate("/contact")}>
                  Design My Campus Network <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Deployment Timeline ── */}
      <section className="at-timeline">
        <div className="at-shell">
          <div className="at-timeline__head">
            <div className="at-label at-label--blue" style={{ margin:"0 auto 1rem" }}><span className="at-dot" /> Deployment Lifecycle</div>
            <h2 className="at-title">From Assessment to Operational Network</h2>
            <p className="at-sub" style={{ margin:"0 auto", maxWidth:"600px" }}>ALG Data Guard follows a structured five-phase methodology for every Allied Telesis deployment — ensuring every network is engineered, validated, and handed over with full documentation before production go-live.</p>
          </div>
          <div className="at-timeline__track">
            {timelineItems.map((item, idx) => (
              <div key={item.title} className="at-tl-item">
                {item.side === "left" ? (
                  <>
                    <div className="at-tl-item__content">
                      <div className="at-tl-item__phase">{item.phase}</div>
                      <h3 className="at-tl-item__title">{item.title}</h3>
                      <p className="at-tl-item__desc">{item.desc}</p>
                    </div>
                    <div className="at-tl-item__node">{item.icon}</div>
                    <div className="at-tl-item__empty" />
                  </>
                ) : (
                  <>
                    <div className="at-tl-item__empty" />
                    <div className="at-tl-item__node">{item.icon}</div>
                    <div className="at-tl-item__content at-tl-item__content--right">
                      <div className="at-tl-item__phase">{item.phase}</div>
                      <h3 className="at-tl-item__title">{item.title}</h3>
                      <p className="at-tl-item__desc">{item.desc}</p>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="at-industries">
        <div className="at-shell">
          <div className="at-industries__head">
            <div className="at-label at-label--blue"><span className="at-dot" /> Industries</div>
            <h2 className="at-title">Allied Telesis Across Every Vertical</h2>
            <p className="at-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Allied Telesis serves organizations in every industry — with validated reference architectures, compliance documentation, and industry-specific switch configurations for every vertical.</p>
          </div>
          <div className="at-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="at-ind-card">
                <span className="at-ind-card__icon">{ind.icon}</span>
                <h3 className="at-ind-card__name">{ind.name}</h3>
                <p className="at-ind-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why ALG ── */}
      <section className="at-why at-blueprint-bg">
        <div className="at-shell">
          <div className="at-why__inner">
            <div>
              <div className="at-label at-label--dark"><span className="at-dot" /> Why ALG Data Guard</div>
              <h2 className="at-title at-why__title">The Allied Telesis Advantage, <span>Delivered by Experts</span></h2>
              <p className="at-why__body">As an authorized Allied Telesis partner, ALG Data Guard provides enterprise networking solutions from design through long-term management — with AMF deployment expertise, industrial networking experience, and proactive managed network services that keep your infrastructure operational.</p>
              <div className="at-why__reasons">
                {reasons.map(r => (
                  <div key={r.title} className="at-reason">
                    <span className="at-reason__icon">{r.icon}</span>
                    <div>
                      <div className="at-reason__title">{r.title}</div>
                      <p className="at-reason__desc">{r.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="at-btn at-btn--blue" onClick={() => navigate("/contact")}>
                  Start My Network Project <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=900&q=80"
              alt="Allied Telesis network team"
              className="at-why__img"
            />
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="at-faq">
        <div className="at-shell">
          <div className="at-faq__head">
            <div className="at-label at-label--blue" style={{ margin:"0 auto 1rem" }}><span className="at-dot" /> FAQs</div>
            <h2 className="at-title">Allied Telesis Questions, Answered</h2>
            <p className="at-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Expert answers about Allied Telesis networking, AMF management, industrial Ethernet, and ALG Data Guard's deployment services.</p>
          </div>
          <div className="at-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`at-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="at-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="at-faq-item__q">{faq.q}</span>
                  <span className="at-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="at-faq-item__answer"><div className="at-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="at-cta at-blueprint-bg">
        <div className="at-cta__inner">
          <div className="at-label at-label--dark" style={{ margin:"0 auto 1.2rem" }}><span className="at-dot" /> Get Started</div>
          <h2 className="at-cta__title">
            Build <span>Infrastructure</span><br />That Never Fails.
          </h2>
          <p className="at-cta__sub">Whether you're upgrading a campus network, deploying industrial Ethernet for an OT environment, or implementing AMF for zero-touch multi-site management — ALG Data Guard's Allied Telesis-certified engineers deliver enterprise networking built for the long term.</p>
          <div className="at-cta__actions">
            <button className="at-btn at-btn--blue" onClick={() => navigate("/contact")}>
              Start My Network Design <ArrowIcon />
            </button>
            <button className="at-btn at-btn--ghost" onClick={() => navigate("/contact")}>
              Request Network Assessment
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
